<div class="reg-page">
    @if (students.length > 1) {
        <nav class="student-switcher" role="tablist" [attr.aria-label]="'Select student' | translate">
            @for (student of students; track student.student_id) {
                <button type="button" role="tab" class="student-switcher__item"
                    [class.is-active]="student.student_id === studentId"
                    [attr.aria-selected]="student.student_id === studentId"
                    [disabled]="disableAutoRegister"
                    (click)="changeStudent(student)">
                    <span class="avatar">{{ getInitials(student.student_name) }}</span>
                    <span class="student-switcher__name">{{ student.student_name }}</span>
                </button>
            }
        </nav>
    }

    @if (loading.period) {
        <div class="hero-skeleton" aria-busy="true">
            <div class="skeleton skeleton--line" style="width: 180px;"></div>
            <div class="skeleton skeleton--title" style="width: 320px;"></div>
            <div class="d-flex flex-wrap gap-2">
                <div class="skeleton skeleton--chip"></div>
                <div class="skeleton skeleton--chip"></div>
                <div class="skeleton skeleton--chip"></div>
            </div>
        </div>
        <div class="tile-grid mt-3">
            @for (i of [1, 2, 3, 4]; track i) {
                <div class="skeleton skeleton--tile"></div>
            }
        </div>
    } @else {
        <section class="hero">
            <div class="hero__main">
                <span class="hero__eyebrow">{{ 'School Program Registration' | translate }}</span>
                <h1 class="hero__title">{{ selectedStudentName || ('Online Registration' | translate) }}</h1>
                @if (dataPeriod) {
                    <ul class="hero__meta">
                        <li>
                            <i-feather name="calendar"></i-feather>
                            <span class="hero__meta-label">{{ 'Academic Year' | translate }}</span>
                            <strong>{{ dataPeriod.academic_year }}</strong>
                        </li>
                        <li>
                            <i-feather name="map-pin"></i-feather>
                            <span class="hero__meta-label">{{ 'School Location' | translate }}</span>
                            <strong>{{ dataPeriod.school_short_address }}</strong>
                        </li>
                        <li>
                            <i-feather name="layers"></i-feather>
                            <span class="hero__meta-label">{{ 'Year Level' | translate }}</span>
                            <strong>{{ dataPeriod.description }}</strong>
                        </li>
                    </ul>
                }
            </div>
            <div class="hero__aside">
                @if (dataPeriod) {
                    <div class="window-status" [ngClass]="isPeriodUpcoming ? 'is-upcoming' : (isPeriodClosed ? 'is-closed' : 'is-open')">
                        <span class="window-status__dot"></span>
                        <div>
                            @if (isPeriodUpcoming) {
                                <div class="window-status__label">{{ 'Opens soon' | translate }}</div>
                                <div class="window-status__date">{{ 'Opens' | translate }} {{ periodStart | date:'d MMM y, HH:mm' }}</div>
                            } @else if (isPeriodClosed) {
                                <div class="window-status__label">{{ 'Registration has closed' | translate }}</div>
                                <div class="window-status__date">{{ 'Closed on' | translate }} {{ periodEnd | date:'d MMM y, HH:mm' }}</div>
                            } @else {
                                <div class="window-status__label">{{ 'Registration open' | translate }}</div>
                                <div class="window-status__date">{{ 'Closes' | translate }} {{ periodEnd | date:'d MMM y, HH:mm' }}</div>
                            }
                        </div>
                    </div>
                }
                <button type="button" class="hero__action" (click)="onShowParticipated()">
                    <i-feather name="list"></i-feather>
                    {{ 'Show Registered' | translate }}
                    <span class="hero__action-count">{{ participatedActivities.length + pendingActivities.length }}</span>
                </button>
            </div>
        </section>

        @if (!dataPeriod) {
            <div class="panel empty-state">
                <div class="empty-state__icon"><i-feather name="calendar"></i-feather></div>
                <h2 class="empty-state__title">{{ 'No registration period yet' | translate }}</h2>
                <p class="empty-state__text">{{ 'PERIOD_SETUP_NOT_SET' | translate }}</p>
            </div>
        } @else {
            @if (isPeriodUpcoming) {
                <div class="callout callout--info">
                    <i-feather name="clock"></i-feather>
                    <div>
                        <div class="callout__title">{{ 'Registration has not opened yet' | translate }}</div>
                        <div>Registration will be opened from {{ periodStart | date:'medium' }} to {{ periodEnd | date:'medium' }}</div>
                    </div>
                </div>
            } @else if (isPeriodClosed) {
                <div class="callout callout--neutral">
                    <i-feather name="lock"></i-feather>
                    <div>
                        <div class="callout__title">{{ 'Registration has closed' | translate }}</div>
                        <div>{{ 'You can still see the activities and your registration status below.' | translate }}</div>
                    </div>
                </div>
            }

            @if (globalRule || ruleList?.length) {
                <section class="panel">
                    <header class="panel__head">
                        <div>
                            <h2 class="panel__title">
                                <span class="panel__icon panel__icon--rules" aria-hidden="true"><i-feather name="sliders"></i-feather></span>
                                {{ 'Registration Rules' | translate }}
                            </h2>
                        </div>
                    </header>
                    <div class="tile-grid">
                        @if (globalRule) {
                            <ng-container *ngTemplateOutlet="quotaTile; context: {
                                name: ('All Programs' | translate),
                                min: globalRule.minimum_selection,
                                max: globalRule.maximum_selection,
                                used: currentTotalGlobal || 0,
                                highlight: true
                            }"></ng-container>
                        }
                        @for (rule of ruleList; track rule.school_program_id) {
                            <ng-container *ngTemplateOutlet="quotaTile; context: {
                                name: rule.school_program_name,
                                min: rule.minimum_selection,
                                max: rule.maximum_selection,
                                used: getProgramUsed(rule.school_program_id),
                                highlight: false,
                                tone: getProgramTone(rule.school_program_id)
                            }"></ng-container>
                        }
                    </div>
                </section>
            }

            <section class="panel panel--flush">
                <header class="panel__head panel__head--padded">
                    <div>
                        <h2 class="panel__title">
                            <span class="panel__icon panel__icon--activities" aria-hidden="true"><i-feather name="grid"></i-feather></span>
                            {{ 'Activities' | translate }}
                            <span class="panel__count">{{ dataActivity?.length || 0 }}</span>
                        </h2>
                    </div>
                    <label class="search-field">
                        <i-feather name="search"></i-feather>
                        <input type="search" [ngModel]="searchTerm" (ngModelChange)="onSearch($event)"
                            [placeholder]="'Search activity or program' | translate"
                            [attr.aria-label]="'Search activity or program' | translate">
                    </label>
                </header>

                @if (programOptions.length > 1) {
                    <div class="filter-chips" role="group" [attr.aria-label]="'Filter by school program' | translate">
                        <button type="button" class="filter-chip" [class.is-active]="programFilter === null"
                            [attr.aria-pressed]="programFilter === null" (click)="setProgramFilter(null)">
                            {{ 'All' | translate }}<span class="filter-chip__count">{{ dataActivity?.length || 0 }}</span>
                        </button>
                        @for (program of programOptions; track program.id) {
                            <button type="button" class="filter-chip" [class.is-active]="programFilter === program.id"
                                [attr.aria-pressed]="programFilter === program.id" (click)="setProgramFilter(program.id)">
                                {{ program.name }}<span class="filter-chip__count">{{ program.count }}</span>
                            </button>
                        }
                    </div>
                }

                @if (isActivityLoading) {
                    <div class="table-skeleton" aria-busy="true">
                        @for (i of [1, 2, 3, 4, 5]; track i) {
                            <div class="skeleton skeleton--row"></div>
                        }
                    </div>
                } @else if (!dataLoaded) {
                    <div class="empty-state">
                        <div class="empty-state__icon empty-state__icon--danger"><i-feather name="alert-circle"></i-feather></div>
                        <h2 class="empty-state__title">{{ 'Activities could not be loaded' | translate }}</h2>
                        <p class="empty-state__text">{{ 'Please check your connection and try again.' | translate }}</p>
                        <button type="button" class="btn-soft" (click)="getExcul()">
                            <i-feather name="refresh-cw"></i-feather>{{ 'Try again' | translate }}
                        </button>
                    </div>
                } @else if (!visibleActivities.length) {
                    <div class="empty-state">
                        <div class="empty-state__icon"><i-feather name="inbox"></i-feather></div>
                        @if (dataActivity?.length) {
                            <h2 class="empty-state__title">{{ 'No matching activities' | translate }}</h2>
                            <p class="empty-state__text">{{ 'Try another keyword or program filter.' | translate }}</p>
                        } @else {
                            <h2 class="empty-state__title">{{ 'No activities available' | translate }}</h2>
                            <p class="empty-state__text">{{ 'There are no activities open for this student in this period.' | translate }}</p>
                        }
                    </div>
                } @else {
                    <table mat-table [dataSource]="visibleActivities" [fixedLayout]="true" class="activity-table">
                        <colgroup>
                            @if (canCreateRegistration) {
                                <col style="width: 5%;">
                            }
                            <col style="width: 22%;">
                            <col style="width: 15%;">
                            <col style="width: 15%;">
                            <col style="width: 12%;">
                            <col style="width: 11%;">
                            <col style="width: 20%;">
                        </colgroup>

                        @if (canCreateRegistration) {
                            <ng-container matColumnDef="action">
                                <th mat-header-cell *matHeaderCellDef class="cell-select">
                                    <span class="visually-hidden">{{ 'Select' | translate }}</span>
                                </th>
                                <td mat-cell *matCellDef="let element" class="cell-select">
                                    @if (isGenderMismatch(element)) {
                                        <span class="row-icon row-icon--muted" container="body" [ngbTooltip]="'Not eligible - Gender Mismatch' | translate">
                                            <i-feather name="slash"></i-feather>
                                        </span>
                                    } @else if (studentActivityMap[element.school_program_activity_id]?.activity_participant_id) {
                                        <span class="row-icon row-icon--success" container="body" [ngbTooltip]="'Registered' | translate">
                                            <i-feather name="check-circle"></i-feather>
                                        </span>
                                    } @else if (isSelectionDisabled(element)) {
                                        <span class="row-icon row-icon--muted" container="body" [ngbTooltip]="(getActivityStatuses(element)[0]?.label || 'Not available') | translate">
                                            <i-feather name="slash"></i-feather>
                                        </span>
                                    } @else {
                                        <mat-checkbox [checked]="isSelected(element)" [disabled]="disableAutoRegister"
                                            [aria-label]="element.activity_name" (change)="registerCheckbox($event, element)">
                                        </mat-checkbox>
                                    }
                                </td>
                            </ng-container>
                        }

                        <ng-container matColumnDef="activity">
                            <th mat-header-cell *matHeaderCellDef>{{ 'Activity' | translate }}</th>
                            <td mat-cell *matCellDef="let element" class="cell-activity">
                                <span class="program-tag" [ngClass]="getProgramTone(element.school_program_id)">{{ element.school_program_name }}</span>
                                <div class="activity-name">
                                    {{ element.activity_name }}
                                    @if (element.gender === 'M') {
                                        <span class="gender-tag gender-tag--m" container="body" [ngbTooltip]="'Male only' | translate"><i class="fa-solid fa-mars"></i></span>
                                    } @else if (element.gender === 'F') {
                                        <span class="gender-tag gender-tag--f" container="body" [ngbTooltip]="'Female only' | translate"><i class="fa-solid fa-venus"></i></span>
                                    }
                                </div>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="schedule">
                            <th mat-header-cell *matHeaderCellDef>{{ 'Schedule' | translate }}</th>
                            <td mat-cell *matCellDef="let element" [attr.data-label]="'Schedule' | translate">
                                <div class="schedule-list">
                                    @for (day of element.schedule; track $index) {
                                        @if (day.schoolday) {
                                            <div class="schedule-item">
                                                <span class="schedule-item__day">{{ day.schoolday }}</span>
                                                <!-- Time is optional; the day still shows without it -->
                                                @if (day.start_time && day.end_time) {
                                                    <span class="schedule-item__time">{{ day.start_time | date:'HH:mm' }} – {{ day.end_time | date:'HH:mm' }}</span>
                                                } @else if (day.start_time) {
                                                    <span class="schedule-item__time">{{ day.start_time | date:'HH:mm' }}</span>
                                                }
                                            </div>
                                        } @else {
                                            <span class="text-subtle">-</span>
                                        }
                                    } @empty {
                                        <span class="text-subtle">-</span>
                                    }
                                </div>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="session">
                            <th mat-header-cell *matHeaderCellDef>{{ 'Session' | translate }}</th>
                            <td mat-cell *matCellDef="let element" [attr.data-label]="'Session' | translate">
                                <div class="cell-stack">
                                    <span><span class="text-nowrap">{{ element.start_date | date:'d MMM yy' }} –</span> <span class="text-nowrap">{{ element.end_date | date:'d MMM yy' }}</span></span>
                                    @if (element.session_override || element.session) {
                                        <span class="cell-sub">{{ element.session_override ? element.session_override : element.session }} {{ 'session' | translate }}</span>
                                    }
                                </div>
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="quota">
                            <th mat-header-cell *matHeaderCellDef>{{ 'Quota' | translate }}</th>
                            <td mat-cell *matCellDef="let element" [attr.data-label]="'Quota' | translate">
                                @if (hasQuotaLimit(element)) {
                                    <div class="quota">
                                        <div class="quota__nums"><strong>{{ getTaken(element) }}</strong> / {{ element.max_participant }}</div>
                                        <div class="meter meter--sm"
                                            [class.is-warn]="getPercentage(element) >= 75 && getPercentage(element) < 100"
                                            [class.is-full]="getPercentage(element) >= 100">
                                            <span class="meter__fill" [style.width.%]="getPercentage(element)"></span>
                                        </div>
                                        <div class="cell-sub">
                                            @if (isFull(element)) {
                                                {{ 'Full' | translate }}
                                            } @else {
                                                {{ element.max_participant - getTaken(element) }} {{ 'slots left' | translate }}
                                            }
                                        </div>
                                    </div>
                                } @else {
                                    <div class="cell-stack">
                                        <strong>{{ getTaken(element) }}</strong>
                                        <span class="cell-sub">{{ 'participant' | translate }}</span>
                                    </div>
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="fee">
                            <th mat-header-cell *matHeaderCellDef>{{ 'Fee' | translate }}</th>
                            <td mat-cell *matCellDef="let element" [attr.data-label]="'Fee' | translate">
                                @if (+element.participation_fee > 0) {
                                    <span class="fee">Rp {{ element.participation_fee | number }}</span>
                                } @else {
                                    <span class="fee fee--free">{{ 'Free' | translate }}</span>
                                }
                            </td>
                        </ng-container>

                        <ng-container matColumnDef="status">
                            <th mat-header-cell *matHeaderCellDef>{{ 'Status' | translate }}</th>
                            <td mat-cell *matCellDef="let element" class="cell-status" [attr.data-label]="'Status' | translate">
                                <div class="status-stack">
                                    @for (status of getActivityStatuses(element); track $index) {
                                        @if (status.action === 'register') {
                                            <button type="button" class="btn-register" (click)="onStatusAction($event, status, element)">
                                                <i-feather name="plus"></i-feather>{{ status.label | translate }}
                                            </button>
                                        } @else if (status.action === 'payment') {
                                            <button type="button" class="status-pill status-pill--action" [ngClass]="'status-pill--' + status.tone"
                                                (click)="onStatusAction($event, status, element)">
                                                <span class="status-pill__dot"></span>{{ status.label | translate }}<i-feather name="chevron-right"></i-feather>
                                            </button>
                                            @if (status.hint) {
                                                <span class="status-hint">{{ status.hint | translate }}</span>
                                            }
                                        } @else {
                                            <span class="status-pill" [ngClass]="'status-pill--' + status.tone">
                                                <span class="status-pill__dot"></span>
                                                @if (status.program) {
                                                    <span>{{ 'Program' | translate }} <i>{{ status.program }}</i> {{ 'limit reached' | translate }}</span>
                                                } @else {
                                                    {{ status.label | translate }}
                                                }
                                            </span>
                                        }
                                    } @empty {
                                        <span class="text-subtle">-</span>
                                    }
                                </div>
                                @if (getRejectReason(element)) {
                                    <div class="reject-reason">
                                        <span class="reject-reason__label">{{ 'PREVIOUS_REJECT_REASON' | translate }}</span>
                                        {{ getRejectReason(element) }}
                                    </div>
                                }
                            </td>
                        </ng-container>

                        <tr mat-header-row *matHeaderRowDef="columnsToDisplay"></tr>
                        <tr mat-row *matRowDef="let element; columns: columnsToDisplay;"
                            [class.is-selected]="isSelected(element)"
                            [class.is-muted]="isRowMuted(element)"></tr>
                    </table>
                }
            </section>

            @if (canCreateRegistration && checkBox.length > 0 && !isOutsidePeriod) {
                <div class="selection-bar" role="region" [attr.aria-label]="'Selected activities' | translate">
                    <div class="selection-bar__info">
                        <span class="selection-bar__count">{{ checkBox.length }}</span>
                        <div class="selection-bar__text">
                            <strong>{{ 'activities selected' | translate }}</strong>
                            <span class="selection-bar__names">{{ selectedActivityNames }}</span>
                        </div>
                    </div>
                    <div class="selection-bar__actions">
                        <button type="button" class="selection-bar__clear" (click)="clearSelection()" [disabled]="disableAutoRegister">
                            {{ 'Clear' | translate }}
                        </button>
                        <button type="button" class="selection-bar__submit" (click)="onBulkRegist()" [disabled]="disableAutoRegister">
                            @if (disableAutoRegister) {
                                <mat-spinner [diameter]="16"></mat-spinner>
                            } @else {
                                <i-feather name="check"></i-feather>
                            }
                            {{ 'Register' | translate }}
                        </button>
                    </div>
                </div>
            }
        }
    }
</div>

<ng-template #quotaTile let-name="name" let-min="min" let-max="max" let-used="used" let-highlight="highlight" let-tone="tone">
    <div class="quota-tile" [class.quota-tile--highlight]="highlight" [ngClass]="['is-' + getQuotaState(used, min, max), tone || '']">
        <div class="quota-tile__head">
            <span class="quota-tile__name" [title]="name">{{ name }}</span>
            <span class="quota-tile__count">
                <strong>{{ used }}</strong>
                @if (max !== null && max !== undefined) {
                    <span> / {{ max }}</span>
                }
            </span>
        </div>
        @if (max !== null && max !== undefined) {
            <div class="meter">
                <span class="meter__fill" [style.width.%]="getUsagePercent(used, max)"></span>
            </div>
        }
        <div class="quota-tile__foot">
            <span>{{ 'Min' | translate }} {{ min ?? '-' }} · {{ 'Max' | translate }} {{ max ?? '-' }}</span>
            <span class="quota-tile__hint">
                @switch (getQuotaState(used, min, max)) {
                    @case ('full') {
                        {{ 'Limit reached' | translate }}
                    }
                    @case ('short') {
                        {{ +min - used }} {{ 'more needed' | translate }}
                    }
                    @default {
                        @if (max !== null && max !== undefined) {
                            {{ +max - used }} {{ 'left' | translate }}
                        }
                    }
                }
            </span>
        </div>
    </div>
</ng-template>
<ng-template #paymentModal>
    <div class="modal-header">
        <h5 class="modal-title payment-title">{{ 'Payment Details' | translate }}</h5>
        <button type="button" class="btn-close" (click)="closePaymentSection()" aria-label="Close"></button>
    </div>
    <div class="modal-body payment-section">
        <div class="mb-3">
            <div class="fw-bold fs-6">{{ 'Activity' | translate }} : {{ activityRegistrationName }}</div>
        </div>

        <form [formGroup]="paymentForm">
            <div class="row mb-1">
                <mat-form-field appearance="outline" class="w-100">
                    <mat-label>{{ 'Payment Method' | translate }}</mat-label>
                    <mat-select formControlName="payment_method" (selectionChange)="onChangePaymentMethod($event.value)">
                        <mat-option *ngFor="let method of paymentMethods" [value]="method">
                            {{ method | translate }}
                        </mat-option>
                    </mat-select>
                    <mat-error>{{ 'Payment Method is required' | translate }}</mat-error>
                </mat-form-field>
            </div>
        </form>

        @if (isBankTransfer) {
            <mat-tab-group animationDuration="200ms">
                <mat-tab [label]="'PAYMENT_SUBMISSION' | translate">
                    <div class="pt-3">
                        @if (isLatestPaymentApproved) {
                            <div class="alert alert-success d-flex align-items-center gap-2 mb-3" role="alert">
                                <span>{{ 'PAYMENT_ALREADY_APPROVED_MSG' | translate }}</span>
                            </div>
                        }
                        <form [formGroup]="paymentForm">
                            <div class="row mb-3">
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>{{ 'Bank Account Holder Name' | translate }}</mat-label>
                                    <input matInput placeholder="{{ 'Bank Account Holder Name' | translate }}" formControlName="bank_account_holder_name" [readonly]="isLatestPaymentApproved">
                                    <mat-error>{{ 'Bank Account Holder Name is required' | translate }}</mat-error>
                                </mat-form-field>
                            </div>
                            <div class="row mb-3">
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>{{ 'Bank Account Number' | translate }}</mat-label>
                                    <input matInput placeholder="{{ 'Account Number' | translate }}" formControlName="bank_account_number" [readonly]="isLatestPaymentApproved">
                                    <mat-error>{{ 'Bank Account Number is required' | translate }}</mat-error>
                                </mat-form-field>
                            </div>
                            <div class="row mb-3">
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>{{ 'BANK_NAME' | translate }}</mat-label>
                                    <input matInput placeholder="{{ 'BANK_NAME' | translate }}" formControlName="bank_name" [readonly]="isLatestPaymentApproved">
                                    <mat-error>{{ 'BANK_NAME_REQUIRED' | translate }}</mat-error>
                                </mat-form-field>
                            </div>
                            <div class="row mb-1">
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>{{ 'Payment Amount' | translate }}</mat-label>
                                    <input matInput placeholder="{{ 'Amount' | translate }}" formControlName="payment_amount" type="number" readonly>
                                    <mat-error>{{ 'Payment Amount is required' | translate }}</mat-error>
                                </mat-form-field>
                            </div>
                            <div class="row mb-3">
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>{{ 'Payment Date' | translate }}</mat-label>
                                    <mat-hint>{{ 'MM/DD/YYYY' | translate }}</mat-hint>
                                    <input matInput [matDatepicker]="picker" formControlName="payment_date" [readonly]="isLatestPaymentApproved">
                                    <mat-datepicker-toggle matIconSuffix [for]="picker" [disabled]="isLatestPaymentApproved"></mat-datepicker-toggle>
                                    <mat-datepicker #picker></mat-datepicker>
                                    <mat-error>{{ 'Payment Date is required' | translate }}</mat-error>
                                </mat-form-field>
                            </div>
                            @if (!isLatestPaymentApproved) {
                                <div class="row mb-3">
                                    <div>{{ 'Payment Slip' | translate }}<span class="red">*</span></div>
                                    <input accept=".jpg, .jpeg, .png, .pdf" id="slip" type="file" class="custom-file-input"
                                        (change)="onChangeDigitalFile($event)" />
                                    @if (paymentForm.get('payment_slip').value) {
                                        <div class="mt-2 mb-2">
                                            <a [href]="paymentForm.get('payment_slip').value.url" target="_blank">
                                                {{ 'VIEW_PAYMENT_SLIP' | translate }}
                                            </a>
                                        </div>
                                    }
                                </div>
                            }
                            <div class="row mb-2">
                                <mat-form-field appearance="outline" class="w-100">
                                    <mat-label>{{ 'Payment Notes' | translate }}</mat-label>
                                    <input matInput placeholder="{{ 'Notes' | translate }}" formControlName="payment_notes" [readonly]="isLatestPaymentApproved">
                                </mat-form-field>
                            </div>
                        </form>
                    </div>
                </mat-tab>

                <mat-tab [label]="'PAYMENT_HISTORY' | translate">
                    <div class="pt-3">
                        <app-payment-manual-logs-table
                            [logs]="registrationPaymentDetail?.payment_manual_logs || []">
                        </app-payment-manual-logs-table>
                    </div>
                </mat-tab>
            </mat-tab-group>
        }

        @if (isVirtualAccount) {
            <div class="d-flex justify-content-center">
                @if (loading.va) {
                    <mat-spinner></mat-spinner>
                }
            </div>
            @if (!loading.va && registrationPaymentDetail) {
                <div>
                    <div class="row mb-3">
                        <div class="fw-bold">{{ 'VA_PAYMENT_HAS_BEEN_GENERATED' | translate }}</div>
                        <div>
                            {{ registrationPaymentDetail.payment_generated ? 'Yes' : 'No' }}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="fw-bold">{{ 'Va Number' | translate }}</div>
                        <div>
                            {{ registrationPaymentDetail.va_number || '-' }}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="fw-bold">{{ 'Total Fee' | translate }}</div>
                        <div>
                            Rp {{ (registrationPaymentDetail.total_fee | number) || 0 }}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="fw-bold">{{ 'Payment Date' | translate }}</div>
                        <div>
                            {{ (registrationPaymentDetail.payment_date | date: 'dd/MM/yyyy') || '-' }}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="fw-bold">{{ 'Payment Notes' | translate }}</div>
                        <div>
                            {{ registrationPaymentDetail.payment_notes || '-' }}
                        </div>
                    </div>
                </div>
            }
            @if (!loading.va && !registrationPaymentDetail) {
                <div>
                    <span>{{ 'NO_PAYMENT_DATA' | translate }}</span>
                </div>
            }
        }
    </div>
    @if (isBankTransfer && !isLatestPaymentApproved) {
        <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="closePaymentSection()">{{ 'Cancel' | translate }}</button>
            @if (auth.permissions['SCHOOL_PROGRAM_REGISTRATION_PAYMENT_STUDENT_CREATE']) {
                <button type="button" class="btn btn-success" (click)="onConfirmManualPayment()">
                    @if (!loading.payment) {
                        <div>{{ 'Confirm' | translate }}</div>
                    }
                    @if (loading.payment) {
                        <mat-spinner [diameter]="25"></mat-spinner>
                    }
                </button>
            }
        </div>
    }
    @if (isBankTransfer && isLatestPaymentApproved) {
        <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="closePaymentSection()">{{ 'Cancel' | translate }}</button>
        </div>
    }
</ng-template>

<ng-template #participationSlotModal let-modal>
    <div class="reg-modal">
        <div class="reg-modal__header">
            <div>
                <h5 class="reg-modal__title">{{ 'My Activities' | translate }}</h5>
                @if (selectedStudentName) {
                    <div class="reg-modal__subtitle">{{ selectedStudentName }}</div>
                }
            </div>
            <button type="button" class="btn-close" (click)="modal.dismiss()" aria-label="Close"></button>
        </div>
        <div class="reg-modal__body">
            <section class="reg-modal__group">
                <h6 class="reg-modal__group-title">
                    {{ 'Participated Activity' | translate }}
                    <span class="panel__count">{{ participatedActivities.length }}</span>
                </h6>
                @for (activity of participatedActivities; track activity.school_program_activity_id) {
                    <div class="reg-modal__item">
                        <div class="reg-modal__item-text">
                            <span class="activity-name">{{ activity.activity_name }}</span>
                            <span class="cell-sub">{{ activity.school_program_name }}</span>
                        </div>
                        <span class="status-pill status-pill--success"><span class="status-pill__dot"></span>{{ 'Active' | translate }}</span>
                    </div>
                } @empty {
                    <div class="reg-modal__empty">{{ 'No active activities yet' | translate }}</div>
                }
            </section>
            <section class="reg-modal__group">
                <h6 class="reg-modal__group-title">
                    {{ 'Registered Activity' | translate }}
                    <span class="panel__count">{{ pendingActivities.length }}</span>
                </h6>
                @for (activity of pendingActivities; track activity.school_program_activity_id) {
                    <div class="reg-modal__item">
                        <div class="reg-modal__item-text">
                            <span class="activity-name">{{ activity.activity_name }}</span>
                            <span class="cell-sub">{{ activity.school_program_name }}</span>
                        </div>
                        <span class="status-pill status-pill--info"><span class="status-pill__dot"></span>{{ 'Registering' | translate }}</span>
                    </div>
                } @empty {
                    <div class="reg-modal__empty">{{ 'No pending registrations' | translate }}</div>
                }
            </section>
        </div>
    </div>
</ng-template>

<ng-template #ruleModal>
    <div>
        <div class="d-flex justify-content-center mt-4 mb-2 fw-bold fs-3">{{ 'Rules' | translate }}</div>
        <div class="m-4">
            <div class="mb-4">
                <div class="fs-5 fw-bold mb-1">{{ globalRule?.school_short_address }}</div>
                <div class="fs-5 fw-bold">School Location Global Rules</div>
                <div class="fw-bold">Minimum Activity : {{ globalRule?.minimum_selection ?? '-' }}</div>
                <div class="fw-bold">Maximum Actitity : {{ globalRule?.maximum_selection ?? '-' }}</div>
            </div>
            <div class="mb-4">
                <!-- <ng-container *ngFor="let program of ruleList">
                    <div><b>{{ program.school_program_name }}</b></div>
                    <div class="d-flex mb-2">
                        <div class="col-12 col-md-10">
                            <div class="row mb-2">Minimum Activity</div>
                            <div class="row mb-2">Maximum Activity</div>
                        </div>
                        <div class="col-12 col-md-2">
                            <div class="row mb-2">{{ program.minimum_selection || '-' }}</div>
                            <div class="row mb-2">{{ program.maximum_selection || '-' }}</div>
                        </div>
                    </div>
                </ng-container> -->
                <div class="fs-5 fw-bold mb-1">School Program Rules</div>
                <table table class="table table-striped" style="width: 80%;">
                    <colgroup>
                        <col style="width: 40%;">
                        <col style="width: 20%;">
                        <col style="width: 20%;">
                    </colgroup>
                    <thead class="ms-1">
                        <th class="fw-bold">School Program Name</th>
                        <th class="fw-bold">Minimum Activity</th>
                        <th class="fw-bold">Maximum Activity</th>
                    </thead>
                    <tbody>
                        <tr *ngFor="let program of ruleList">
                            <td>{{ program.school_program_name || '-' }}</td>
                            <td>{{ program.minimum_selection ?? '-' }}</td>
                            <td>{{ program.maximum_selection ?? '-' }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            @if (!ruleMap) {
                <div class="mb-2">
                    <b>Rule</b> : -
                </div>
            }
            <div>
                <div class="fs-5 fw-bold mb-2"><b>Participated and Registered in this period</b></div>
                <table class="table table-striped" style="width: 80%;">
                    <colgroup>
                        <col style="width: 40%;">
                        <col style="width: 20%;">
                        <col style="width: 20%;">
                    </colgroup>
                    <thead class="ms-1">
                        <th class="fw-bold">{{ 'School Program' | translate }}</th>
                        <th class="fw-bold">{{ 'Registered' | translate }}</th>
                        <th class="fw-bold">{{ 'Participated' | translate }}</th>
                    </thead>
                    <tbody>
                        <tr *ngFor="let data of studentParticipatedRegistered; let i = index">
                            <ng-container>
                                <td>{{ data.school_program_name || '-' }}</td>
                                <td>{{ data.total_registered || '-' }}</td>
                                <td>{{ data.total_participated || '-' }}</td>
                            </ng-container>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</ng-template>

<ng-template #listTemplate>
    <div>
        <div>{{ '-' | translate }}</div>
    </div>
</ng-template>
